import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState, useAppDispatch, useAppSelector } from 'app/store';
import {
  getContributionClassesAndRolesDDL,
  getContributionClassRoles,
  getPopulatedContributionRoles,
} from 'app/store/SongList/selectors';
import { AsyncThunk } from '@reduxjs/toolkit';

export const useIsMounted = () => {
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => mountedRef.current, []);
};

export const useContributionRolesDDL = () => {
  const classesAndRolesDDL = useAppSelector(getContributionClassesAndRolesDDL);
  const classRoles = useAppSelector(getContributionClassRoles);

  return useMemo(
    () => getPopulatedContributionRoles(classRoles || {}, classesAndRolesDDL),
    [classesAndRolesDDL, classRoles]
  );
};

interface UseResourceHookOptions {
  useCache?: boolean;
  shouldFetch?: boolean;
  effectArgs?: any;
  onResolved?: () => void;
}

const defaultOptions: UseResourceHookOptions = {
  useCache: true,
  shouldFetch: true,
};

const getInitialLoading = <T>(options: UseResourceHookOptions, resource: T) => {
  const { shouldFetch, useCache } = options;
  if (!shouldFetch) {
    return false;
  }
  return useCache ? !resource : true;
};

export const useResource = <T>(
  effect: AsyncThunk<any, void | any, { state: AppState }>,
  selector: (state: AppState) => T,
  options?: UseResourceHookOptions
): [T, boolean] => {
  const { useCache, shouldFetch, effectArgs, onResolved = () => {} } = {
    ...defaultOptions,
    ...(options || {}),
  };
  const dispatch = useAppDispatch();
  const isMounted = useIsMounted();
  const resource = useAppSelector(selector);
  const [loading, setLoading] = useState(
    getInitialLoading({ useCache, shouldFetch }, resource)
  );
  const fetchResource = useCallback(async () => {
    if ((resource && useCache) || !shouldFetch) {
      onResolved();
      return;
    }
    setLoading(true);
    await dispatch(effect(effectArgs));
    onResolved();
    if (!isMounted()) {
      return;
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, effect, isMounted, shouldFetch, useCache]);

  useEffect(() => {
    fetchResource();
  }, [fetchResource]);

  return [resource, loading];
};
